import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css/github-markdown.css';
import { CtyButton, formatTimestamp, formatPageLabels, SourceElementType, checkType, CtyShowToast, sharedPDFViewHandler, pdfViewPayload } from '@circuitry-ai/doc-ui';
import { usePostExplainerTraceAndReasoningMutation, useGetPdfLinkForConversationsMutation } from '@circuitry-ai/doc-data';
import { useState } from 'react';

interface ExplainerAnswerProps {
    answer: string;
    timeTaken: number;
    messageId: string;
    query: string;
    sourceIds?: any[];
    advisorId?: string;
    time_taken_answer?: string;
    llm_model_answer?: string;
}

const ExplainerAnswer = ({ answer, timeTaken, messageId, query, sourceIds = [], advisorId = '', time_taken_answer , llm_model_answer }: ExplainerAnswerProps) => {

    const [postExplainerTraceAndReasoning] = usePostExplainerTraceAndReasoningMutation();
    const [getPdfLink] = useGetPdfLinkForConversationsMutation();
    const [actionDetails, setActionDetails] = useState<any>(null);
    const [actionLoading, setActionLoading] = useState<any>(null);
    const [inlineTriggerLoading, setInlineTriggerLoading] = useState<string>('');

    const handleSourceClick = async (source: any, refId: string) => {
        setInlineTriggerLoading(refId);
        if (source?.element_type === SourceElementType.YOUTUBE || source?.element_type === SourceElementType.URL || source?.element_type === SourceElementType.API) {
            window.open(source?.element_refs?.[0]?.url, '_blank');
            setInlineTriggerLoading('');
        } else {
            const isDitaMap = source?.element_refs?.[0]?.file_type === 'ditamap';
            const sourceIdToUse = isDitaMap && source?.element_refs?.[0]?.sub_source_id
                ? source.element_refs[0].sub_source_id
                : source?.source_id;
            const pageLabels = source?.page_labels?.length > 0 ? [Number(source.page_labels[0])] : [];
            const apiPayLoad: pdfViewPayload = {
                advisor_id: advisorId,
                source_ids: [{ source_id: sourceIdToUse, page_labels: pageLabels }],
            };
            const loadingState = sourceIdToUse + (pageLabels.length > 0 ? pageLabels[0] : '') + messageId + refId;
            sharedPDFViewHandler(loadingState, () => {}, apiPayLoad, getPdfLink, sourceIds, source?.element_type, source, setInlineTriggerLoading);
        }
    };

    const CustomLink = ({ node, children, ...props }: any) => {
        const href = props.href;
        if (href?.includes('ref_id=')) {
            const params = new URLSearchParams(href);
            const refId = params.get('ref_id');
            if (refId) {
                const source = sourceIds?.find((s: any) => s.ref_id == refId);
                let tooltipContent = '';
                if (source) {
                    const hasPages = (source?.element_type === SourceElementType.TEXT || source?.element_type === SourceElementType.TABLE) && source?.page_labels?.length > 0;
                    tooltipContent = hasPages 
                        ? `${source?.file_name}: ${source?.page_labels.length > 1 ? "pages" : "page"}(${formatPageLabels(source?.page_labels)})` 
                        : `${source?.file_name}`;
                }
                if (source) {
                    return (
                        <CtyButton
                            className="text-[#0BA5EC] hover:bg-content1 px-0 font-normal"
                            size="sm"
                            variant="light"
                            onPress={() => handleSourceClick(source, refId)}
                            isLoading={inlineTriggerLoading === refId}
                            tooltipContent={tooltipContent}
                            toolTipColor='warning'
                        >
                            {`[${children}]`}
                        </CtyButton>
                    );
                } else {
                    return (
                        <CtyButton
                            className="text-[#0BA5EC] hover:bg-content1 px-0 font-normal"
                            size="sm"
                            variant="light"
                            onPress={() => CtyShowToast(checkType.error, `Reference source not found`)}
                        >
                            {`[${children}]`}
                        </CtyButton>
                    );
                }
            }
        }
        return <a {...props}>{children}</a>;
    };

    const markdownComponents = {
        ul: ({ node, ...props }: any) => (
            <ul className="list-disc ml-4" {...props} />
        ),
        ol: ({ node, ...props }: any) => (
            <ol className="list-decimal ml-4" {...props} />
        ),
        p: (props: any) => (
            <p className="my-1" {...props} />
        ),
        a: CustomLink,
    };

    const handleActionExecute = async (request_type: number) => {
        try {
            setActionLoading(request_type);
            const payload = {
                answer_explainer: {
                    message_id: messageId,
                    answer,
                    query,
                    request_type,
                    llm_config: {
                        config_id: "pc-rerank-335844",
                        model: "meta-llama/llama-4-maverick-17b-128e-instruct"
                    }
                }
            };

            const data = await postExplainerTraceAndReasoning({ payload, messageId }).unwrap();
            let baseMap: Record<string, any> = {};
            if (Array.isArray(data)) {
                baseMap = data.reduce((acc: any, obj: any) => {
                    const [key, value] = Object.entries(obj)[0] || [];
                    if (key) acc[key] = value;
                    return acc;
                }, {});
            }
            setActionDetails(baseMap);
        } catch (error) {
            console.log(error);
        } finally {
            setActionLoading(null);
        }
    }

    const renderMarkDown = (answer: string) => {
        return (
            <div className={`markdown-body border-1 p-3 rounded w-full`}
                style={{
                    color: 'black',
                    backgroundColor: 'transparent',
                    display: 'block',
                    maxWidth: '100%',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                }}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        ...markdownComponents,
                        table: ({ node, children, ...props }) => (
                            <div className="overflow-x-auto my-2">
                                <table style={{ whiteSpace: 'nowrap' }} {...props}>{children}</table>
                            </div>
                        ),
                    }}
                >
                    {answer}
                </ReactMarkdown>
            </div>
        )
    }

    const actionButtons = [
        { label: 'Trace', requestType: 0 },
        { label: 'Reason', requestType: 1 },
        { label: 'Trace & Reason', requestType: 2 }
    ];

    return (
        <div className="flex flex-col h-full overflow-y-auto gap-3">
            <div className="flex flex-col gap-1">
            <div className="flex w-full flex-row items-center justify-between">
                <div className="font-medium text-lg">Advisor generated answer</div>
                {time_taken_answer && <div className="text-sm"><span className="font-medium">Time Taken:</span> {time_taken_answer} S</div>}
            </div>    
            <div className="flex w-full flex-row items-center justify-between">
                <div className="text-sm font-normal">Answer Generated is based on ranked context</div>
                {llm_model_answer && <div className="text-sm"><span className="font-medium">LLM Model:</span> {llm_model_answer}</div>}
            </div>
            </div>
            <div className='flex-1 overflow-y-auto'>
                {renderMarkDown(answer)}
                <div className="text-sm font-normal mt-1">Time taken: {formatTimestamp(timeTaken)} seconds</div>
            </div>
            {
                <>
                    {actionDetails?.trace_explainer && renderMarkDown(actionDetails?.trace_explainer)}
                    {actionDetails?.reason_explainer && renderMarkDown(actionDetails?.reason_explainer)}
                    {actionDetails?.explainer && renderMarkDown(actionDetails?.explainer)}
                </>
            }
            <div className='flex gap-2'>
                {actionButtons.map(({ label, requestType }) => (
                    <CtyButton
                        key={label}
                        className="border-1"
                        variant="bordered"
                        radius="sm"
                        size="sm"
                        color="primary"
                        onPress={() => handleActionExecute(requestType)}
                        isLoading={actionLoading === requestType}
                    >
                        {label}
                    </CtyButton>
                ))}
            </div>
        </div>
    );
};

export default ExplainerAnswer;